/**
 * Shared shapes, defaults and validation for the workflow author (F7).
 *
 * `validateWorkflow` mirrors `structural_errors` in
 * `backend/app/models/workflow.py`. The server is the authority — it rejects a
 * broken definition with a 400 either way — but repeating the rules here lets
 * the editor disable Save and say *why* instead of letting you discover it on
 * submit. If the two ever disagree, the server wins.
 */

export const STATE_CATEGORIES = ['todo', 'in_progress', 'done', 'cancelled'] as const;
export const ENTITY_TYPES = ['workitem', 'release', 'risk', 'opportunity', 'project'] as const;
export const WORK_ITEM_TYPES = ['epic', 'feature', 'story', 'task', 'bug', 'security', 'idea'] as const;

export const GUARD_TYPES = [
  { type: 'allLinkedPrsMerged', label: 'All linked pull requests merged' },
  { type: 'allChildrenDone', label: 'All child items done' },
  { type: 'dependenciesDone', label: 'Every blocking dependency done' },
  { type: 'noOpenCriticalSecurity', label: 'No unresolved high/critical security items' },
] as const;

export const ACTION_TYPES = [
  { type: 'stampNow', label: 'Stamp the current time on a field' },
  { type: 'setField', label: 'Set a field to a value' },
  { type: 'clearField', label: 'Clear a field' },
] as const;

/** Wildcard source: "from anywhere". Keeps a permissive workflow terse. */
export const ANY_STATE = '*';

/** A 'state' is somewhere an item rests; a 'decision' is passed through. */
export const NODE_KINDS = ['state', 'decision'] as const;
export type NodeKind = (typeof NODE_KINDS)[number];

/** Operators the server's condition evaluator understands. */
export const CONDITION_OPERATORS = [
  { op: 'eq', label: 'is' },
  { op: 'ne', label: 'is not' },
  { op: 'in', label: 'is one of', list: true },
  { op: 'nin', label: 'is not one of', list: true },
  { op: 'gt', label: 'is greater than' },
  { op: 'gte', label: 'is at least' },
  { op: 'lt', label: 'is less than' },
  { op: 'lte', label: 'is at most' },
  { op: 'contains', label: 'contains' },
  { op: 'empty', label: 'is empty', unary: true },
  { op: 'notEmpty', label: 'is set', unary: true },
] as const;

/** Fields worth offering by name; anything else can be typed as a path. */
export const COMMON_FIELDS = [
  'priority', 'status', 'assignee', 'storyPoints', 'workItemType', 'tags',
  'dueDate', 'businessValue', 'customFields.severity', 'customFields.remediationStatus',
] as const;

/** Mirrors the server grammar in backend/app/services/conditions.py. */
export type Condition =
  | { all: Condition[] }
  | { any: Condition[] }
  | { not: Condition }
  | { field: string; op: string; value?: unknown }
  | { guard: string }
  | null;

export const CATEGORY_LABELS: Record<string, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  done: 'Done',
  cancelled: 'Cancelled',
};

export const CATEGORY_BADGE: Record<string, string> = {
  todo: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
  in_progress: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  done: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  cancelled: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
};

export interface XY { x: number; y: number }

export interface WorkflowState {
  id: string;
  label: string;
  /** Canvas position; undefined until the node is moved in the graph view. */
  position?: XY | null;
  /** 'decision' nodes are routed through, never rested on. */
  kind?: NodeKind;
  color: string;
  order: number;
  category: string;
  wipLimit: number;
  slaHours: number;
  isInitial: boolean;
  isTerminal: boolean;
  requiredFields: string[];
}

export interface WorkflowAction {
  type: string;
  field: string;
  value: unknown;
}

export interface WorkflowTransition {
  id: string;
  name: string;
  from: string[];
  to: string;
  requiredPermission: string;
  requiredRole: string;
  requiredFields: string[];
  guards: { type: string; config?: Record<string, unknown> }[];
  actions: WorkflowAction[];
  /** Branch condition, evaluated when leaving a decision node. */
  when?: Condition;
  /** The branch taken when nothing matches. Exactly one per decision. */
  default?: boolean;
  /** Branch evaluation order; first match wins. */
  order?: number;
}

export interface Workflow {
  _id?: string;
  name: string;
  description: string;
  entityType: string;
  appliesTo: string[];
  organizationId: string;
  states: WorkflowState[];
  transitions: WorkflowTransition[];
  version?: number;
  active: boolean;
  isDefault: boolean;
  forbidForce: boolean;
  lockedFields: string[];
}

const PALETTE = ['#94a3b8', '#6366f1', '#0ea5e9', '#14b8a6', '#22c55e', '#f59e0b', '#ef4444'];

/** A URL-ish, stable id from a human label. */
export function slugify(label: string): string {
  return label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
}

/** A unique id within a set, by suffixing. */
export function uniqueId(base: string, taken: string[]): string {
  const seed = base || 'state';
  if (!taken.includes(seed)) return seed;
  for (let n = 2; n < 500; n += 1) {
    const candidate = `${seed}_${n}`;
    if (!taken.includes(candidate)) return candidate;
  }
  return `${seed}_${taken.length}`;
}

export function blankState(order: number, taken: string[]): WorkflowState {
  return {
    id: uniqueId('state', taken),
    label: 'New state',
    color: PALETTE[order % PALETTE.length],
    order,
    category: 'todo',
    wipLimit: 0,
    slaHours: 0,
    isInitial: false,
    isTerminal: false,
    requiredFields: [],
    kind: 'state',
    position: null,
  };
}

/** A decision node: transient, so it carries no WIP, SLA or start/end marker. */
export function blankDecision(order: number, taken: string[]): WorkflowState {
  return {
    ...blankState(order, taken),
    id: uniqueId('decision', taken),
    label: 'Decision',
    color: '#f59e0b',
    kind: 'decision',
  };
}

export function blankTransition(taken: string[], from: string, to: string): WorkflowTransition {
  return {
    id: uniqueId('move', taken),
    name: 'Move',
    from: from ? [from] : [],
    to,
    requiredPermission: '',
    requiredRole: '',
    requiredFields: [],
    guards: [],
    actions: [],
    when: null,
    default: false,
    order: 0,
  };
}

/** A minimal workflow that already satisfies the server's structural rules. */
export function blankWorkflow(): Workflow {
  return {
    name: 'New workflow',
    description: '',
    entityType: 'workitem',
    appliesTo: [],
    organizationId: '',
    active: true,
    isDefault: false,
    forbidForce: false,
    lockedFields: [],
    states: [
      { id: 'todo', label: 'To do', color: PALETTE[0], order: 0, category: 'todo', wipLimit: 0, slaHours: 0, isInitial: true, isTerminal: false, requiredFields: [], position: null },
      { id: 'in_progress', label: 'In progress', color: PALETTE[1], order: 1, category: 'in_progress', wipLimit: 0, slaHours: 0, isInitial: false, isTerminal: false, requiredFields: [], position: null },
      { id: 'done', label: 'Done', color: PALETTE[4], order: 2, category: 'done', wipLimit: 0, slaHours: 0, isInitial: false, isTerminal: true, requiredFields: [], position: null },
    ],
    transitions: [
      { id: 'start', name: 'Start', from: ['todo'], to: 'in_progress', requiredPermission: '', requiredRole: '', requiredFields: [], guards: [], actions: [] },
      { id: 'finish', name: 'Finish', from: ['in_progress'], to: 'done', requiredPermission: '', requiredRole: '', requiredFields: [], guards: [], actions: [] },
    ],
  };
}

/** Fill in anything an older stored document is missing. */
export function normalizeWorkflow(raw: any): Workflow {
  const states: WorkflowState[] = (raw?.states || []).map((s: any, i: number) => ({
    id: s.id || '',
    label: s.label || s.id || '',
    color: s.color || PALETTE[i % PALETTE.length],
    order: typeof s.order === 'number' ? s.order : i,
    category: s.category || 'todo',
    wipLimit: s.wipLimit || 0,
    slaHours: s.slaHours || 0,
    isInitial: Boolean(s.isInitial),
    isTerminal: Boolean(s.isTerminal),
    requiredFields: s.requiredFields || [],
    kind: s.kind === 'decision' ? 'decision' : 'state',
    position:
      s.position && typeof s.position.x === 'number' && typeof s.position.y === 'number'
        ? { x: s.position.x, y: s.position.y }
        : null,
  }));
  return {
    _id: raw?._id,
    name: raw?.name || '',
    description: raw?.description || '',
    entityType: raw?.entityType || 'workitem',
    appliesTo: raw?.appliesTo || [],
    organizationId: raw?.organizationId || '',
    version: raw?.version,
    active: raw?.active !== false,
    isDefault: Boolean(raw?.isDefault),
    forbidForce: Boolean(raw?.forbidForce),
    lockedFields: Array.isArray(raw?.lockedFields) ? raw.lockedFields : [],
    states: [...states].sort((a, b) => a.order - b.order),
    transitions: (raw?.transitions || []).map((t: any) => ({
      id: t.id || '',
      name: t.name || '',
      // Stored as `from`; marshmallow calls it `from_` internally.
      from: t.from || t.from_ || [],
      to: t.to || '',
      requiredPermission: t.requiredPermission || '',
      requiredRole: t.requiredRole || '',
      requiredFields: t.requiredFields || [],
      when: t.when ?? null,
      default: Boolean(t.default),
      order: typeof t.order === 'number' ? t.order : 0,
      guards: t.guards || [],
      actions: (t.actions || []).map((a: any) => ({
        type: a.type, field: a.field || '', value: a.value ?? '',
      })),
    })),
  };
}

export interface ValidationProblem {
  field: 'name' | 'states' | 'transitions';
  message: string;
}

/** Mirrors `structural_errors` server-side. Empty means the server will accept it. */
export function validateWorkflow(wf: Workflow): ValidationProblem[] {
  const out: ValidationProblem[] = [];

  if (!wf.name.trim()) out.push({ field: 'name', message: 'A workflow needs a name.' });

  if (!wf.states.length) {
    out.push({ field: 'states', message: 'A workflow needs at least one state.' });
    return out;
  }

  const ids = wf.states.map((s) => s.id);
  if (ids.some((id) => !id.trim())) {
    out.push({ field: 'states', message: 'Every state needs an id.' });
  }
  const duplicates = [...new Set(ids.filter((id) => ids.filter((o) => o === id).length > 1))];
  if (duplicates.length) {
    out.push({ field: 'states', message: `Duplicate state ids: ${duplicates.join(', ')}.` });
  }

  const initial = wf.states.filter((s) => s.isInitial).length;
  if (initial !== 1) {
    out.push({
      field: 'states',
      message: `Exactly one state must be marked initial (found ${initial}).`,
    });
  }
  if (!wf.states.some((s) => s.isTerminal)) {
    out.push({ field: 'states', message: 'At least one state must be terminal.' });
  }

  const known = new Set(ids);
  wf.transitions.forEach((t) => {
    const label = t.name || t.id || '?';
    if (!t.id.trim()) {
      out.push({ field: 'transitions', message: `Transition "${label}" needs an id.` });
    }
    const unknown = t.from.filter((s) => s !== ANY_STATE && !known.has(s));
    if (unknown.length) {
      out.push({
        field: 'transitions',
        message: `Transition "${label}" comes from unknown state(s): ${unknown.join(', ')}.`,
      });
    }
    if (!t.to || !known.has(t.to)) {
      out.push({
        field: 'transitions',
        message: `Transition "${label}" goes to unknown state "${t.to || '(none)'}".`,
      });
    }
    if (!t.from.length) {
      out.push({
        field: 'transitions',
        message: `Transition "${label}" has no source state — nothing can use it.`,
      });
    }
  });

  out.push(...decisionProblems(wf));
  return out;
}

/** Mirrors `_decision_errors` server-side. */
export function decisionProblems(wf: Workflow): ValidationProblem[] {
  const out: ValidationProblem[] = [];
  const decisions = wf.states.filter((s) => s.kind === 'decision');
  if (!decisions.length) return out;

  decisions.forEach((d) => {
    // A decision is transient, so anything implying an item rests there is a
    // contradiction rather than a harmless extra.
    if (d.wipLimit) {
      out.push({ field: 'states', message: `Decision "${d.id}" cannot carry a WIP limit — nothing rests on it.` });
    }
    if (d.isInitial) {
      out.push({ field: 'states', message: `Decision "${d.id}" cannot be the start state.` });
    }
    if (d.isTerminal) {
      out.push({ field: 'states', message: `Decision "${d.id}" cannot be an end state.` });
    }

    const branches = wf.transitions.filter((t) => t.from.includes(d.id));
    if (!branches.length) {
      out.push({
        field: 'transitions',
        message: `Decision "${d.id}" has no outgoing branch — anything reaching it would be stuck.`,
      });
      return;
    }
    const defaults = branches.filter((t) => t.default);
    if (defaults.length !== 1) {
      out.push({
        field: 'transitions',
        message: `Decision "${d.id}" needs exactly one default branch (found ${defaults.length}) — otherwise an item matching no condition has nowhere to go.`,
      });
    }
  });

  const cycle = findDecisionCycle(wf);
  if (cycle) {
    out.push({
      field: 'transitions',
      message: `Decision nodes form a loop that can never reach a state: ${cycle.join(' → ')}`,
    });
  }
  return out;
}

function findDecisionCycle(wf: Workflow): string[] | null {
  const decisions = new Set(
    wf.states.filter((s) => s.kind === 'decision').map((s) => s.id)
  );
  const edges = new Map<string, string[]>();
  decisions.forEach((d) => edges.set(d, []));
  wf.transitions.forEach((t) => {
    if (!decisions.has(t.to)) return;
    t.from.forEach((f) => {
      if (decisions.has(f)) edges.get(f)!.push(t.to);
    });
  });

  const colour = new Map<string, number>();       // 0 white, 1 grey, 2 black
  decisions.forEach((d) => colour.set(d, 0));
  const stack: string[] = [];

  const visit = (node: string): string[] | null => {
    colour.set(node, 1);
    stack.push(node);
    for (const next of edges.get(node) || []) {
      if (colour.get(next) === 1) return [...stack.slice(stack.indexOf(next)), next];
      if (colour.get(next) === 0) {
        const found = visit(next);
        if (found) return found;
      }
    }
    stack.pop();
    colour.set(node, 2);
    return null;
  };

  for (const d of [...decisions].sort()) {
    if (colour.get(d) === 0) {
      const found = visit(d);
      if (found) return found;
    }
  }
  return null;
}

/** States nothing can reach: a dead column on the board. */
export function unreachableStates(wf: Workflow): string[] {
  const reachable = new Set(wf.states.filter((s) => s.isInitial).map((s) => s.id));
  const wildcard = wf.transitions.some((t) => t.from.includes(ANY_STATE));
  if (wildcard) return [];
  let grew = true;
  while (grew) {
    grew = false;
    wf.transitions.forEach((t) => {
      if (t.from.some((s) => reachable.has(s)) && !reachable.has(t.to)) {
        reachable.add(t.to);
        grew = true;
      }
    });
  }
  return wf.states
    .filter((s) => !reachable.has(s.id) && s.kind !== 'decision')
    .map((s) => s.id);
}

/** Payload for POST/PUT. `order` is rewritten from row position. */
export function toPayload(wf: Workflow): Record<string, unknown> {
  return {
    name: wf.name.trim(),
    description: wf.description,
    entityType: wf.entityType,
    appliesTo: wf.entityType === 'workitem' ? wf.appliesTo : [],
    organizationId: wf.organizationId,
    active: wf.active,
    isDefault: wf.isDefault,
    forbidForce: wf.forbidForce,
    lockedFields: wf.lockedFields,
    states: wf.states.map((s, i) => {
      const { position, ...rest } = s;
      return position ? { ...rest, order: i, position } : { ...rest, order: i };
    }),
    transitions: wf.transitions.map((t, i) => ({
      ...t,
      // The API reads the source list as `from`.
      from: t.from,
      when: t.when ?? null,
      default: Boolean(t.default),
      order: typeof t.order === 'number' ? t.order : i,
      actions: t.actions.map((a) => ({ type: a.type, field: a.field, value: a.value })),
      guards: t.guards.map((g) => ({ type: g.type, config: g.config || {} })),
    })),
  };
}
